import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db, auth } from '../config/firebase';

/**
 * 👤 Perfil y preferencias del administrador.
 *
 * Antes esto vivía SOLO en `localStorage.user`: el modal de Configuración decía
 * "guardado exitosamente" y al cambiar de navegador (o al cerrar sesión, que
 * borraba el almacenamiento) no quedaba nada. Ahora el origen de verdad es el
 * documento `usuarios/{uid}` de Firestore; `localStorage` se conserva solo como
 * caché para que las pantallas puedan leer la preferencia sin pagar una lectura.
 *
 * Campos que este módulo añade a `usuarios/{uid}`:
 *   · `telefono`      (string)
 *   · `preferencias`  ({ avisoNuevasRespuestas: boolean })
 *
 * `nombre` ya existía y lo escribe también `UsersList.js`.
 */

export const CLAVE_PREFERENCIAS = 'ignia-preferencias';
export const EVENTO_PREFERENCIAS = 'ignia-preferencias-change';

export const PREFERENCIAS_POR_DEFECTO = Object.freeze({
  // Aviso emergente en el panel cuando llegan respuestas nuevas (Dashboard.js)
  avisoNuevasRespuestas: true
});

/**
 * Devuelve un objeto de preferencias completo y con tipos correctos,
 * partiendo de lo que haya guardado (o de nada).
 */
export function normalizarPreferencias(datos) {
  const origen = datos && typeof datos === 'object' ? datos : {};
  return {
    avisoNuevasRespuestas:
      origen.avisoNuevasRespuestas === undefined
        ? PREFERENCIAS_POR_DEFECTO.avisoNuevasRespuestas
        : Boolean(origen.avisoNuevasRespuestas)
  };
}

/** uid de la sesión actual; `auth.currentUser` manda y `localStorage` es el respaldo. */
export function uidActual() {
  if (auth.currentUser?.uid) return auth.currentUser.uid;
  try {
    return JSON.parse(localStorage.getItem('user') || 'null')?.uid || null;
  } catch {
    return null;
  }
}

/** Lectura síncrona de la caché local, para pantallas que no quieren esperar a Firestore. */
export function leerPreferenciasLocales() {
  try {
    return normalizarPreferencias(JSON.parse(localStorage.getItem(CLAVE_PREFERENCIAS) || 'null'));
  } catch {
    return normalizarPreferencias(null);
  }
}

function escribirCacheLocal(preferencias) {
  localStorage.setItem(CLAVE_PREFERENCIAS, JSON.stringify(preferencias));
  window.dispatchEvent(new CustomEvent(EVENTO_PREFERENCIAS, { detail: preferencias }));
}

/**
 * Lee `usuarios/{uid}` y refresca la caché local.
 * Devuelve `{ nombre, telefono, preferencias }` y nunca lanza: si Firestore falla
 * se cae a lo que hubiera en `localStorage`, que es mejor que dejar el modal vacío.
 */
export async function cargarPerfilUsuario() {
  const uid = uidActual();
  let usuarioLocal = {};
  try {
    usuarioLocal = JSON.parse(localStorage.getItem('user') || '{}') || {};
  } catch {
    usuarioLocal = {};
  }

  const respaldo = {
    nombre: usuarioLocal.nombre || usuarioLocal.displayName || '',
    telefono: usuarioLocal.telefono || usuarioLocal.phone || '',
    preferencias: leerPreferenciasLocales()
  };

  if (!uid) return respaldo;

  try {
    const snap = await getDoc(doc(db, 'usuarios', uid));
    if (!snap.exists()) return respaldo;

    const datos = snap.data();
    const perfil = {
      nombre: datos.nombre || respaldo.nombre,
      telefono: datos.telefono || '',
      preferencias: normalizarPreferencias(datos.preferencias)
    };
    escribirCacheLocal(perfil.preferencias);
    return perfil;
  } catch (error) {
    console.error('No se pudo leer el perfil del usuario en Firestore:', error);
    return respaldo;
  }
}

/**
 * Guarda nombre, teléfono y preferencias en `usuarios/{uid}` (merge, para no
 * pisar rol/grupo/activo) y actualiza la caché local. Lanza si no hay sesión o
 * si Firestore rechaza la escritura: quien llama debe avisar del fallo.
 */
export async function guardarPerfilUsuario({ nombre, telefono, preferencias }) {
  const uid = uidActual();
  if (!uid) throw new Error('No hay una sesión activa para guardar las preferencias');

  const preferenciasNormalizadas = normalizarPreferencias(preferencias);
  await setDoc(
    doc(db, 'usuarios', uid),
    {
      nombre: (nombre || '').trim(),
      telefono: (telefono || '').trim(),
      preferencias: preferenciasNormalizadas
    },
    { merge: true }
  );

  // Caché local: el objeto `user` alimenta la cabecera del panel
  try {
    const usuarioLocal = JSON.parse(localStorage.getItem('user') || '{}') || {};
    localStorage.setItem(
      'user',
      JSON.stringify({
        ...usuarioLocal,
        nombre: (nombre || '').trim(),
        telefono: (telefono || '').trim()
      })
    );
  } catch (error) {
    console.error('No se pudo actualizar la copia local del usuario:', error);
  }

  escribirCacheLocal(preferenciasNormalizadas);
  return preferenciasNormalizadas;
}
